import type { Suggestion } from "./api";

export function normalizeQuestion(text: string): string {
  return text.normalize("NFKC").toLowerCase().replace(/’/g, "'").trim().replace(/\s+/g, " ").replace(/[?!.]+$/, "");
}

const STOP = new Set("a an and are as at be can could do does for from get how i in is it me my of on or our please tell that the their them this to us we what when where which who why will with would you your about have has mugam guide website answer answers".split(" "));
const GROUPS: Record<string, string> = {};
for (const [topic, words] of Object.entries({
  pricing: "price pricing cost costs pay paid payment payments fee fees charge charges budget expensive",
  setup: "start started starting setup install installation installing embed embedding begin",
  voice: "voice voices audio sound speak speaking speech",
  refund: "refund refunds refunded return returns cancellation cancel",
})) {
  for (const word of words.split(" ")) GROUPS[word] = topic;
}

function tokens(text: string): Set<string> {
  return new Set((text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [])
    .filter((word) => word.length > 2 && !STOP.has(word)).map((word) => GROUPS[word] ?? word));
}

export interface SuggestionContext {
  question?: string;
  answer?: string;
  page_url?: string;
  anchor?: string;
}

/** Rank locally, with no model call; never re-offer an asked/answered FAQ. */
export function selectSuggestions(
  options: Suggestion[], asked: Set<string>, active: string | undefined,
  context: SuggestionContext, limit: number,
): Suggestion[] {
  const excluded = new Set(asked);
  if (active) excluded.add(normalizeQuestion(active));
  const seen = new Set<string>();
  const questionWords = tokens(context.question ?? "");
  const answerWords = tokens(context.answer ?? "");
  return options.flatMap((option, order) => {
    const normalized = normalizeQuestion(option.question);
    if (!normalized || seen.has(normalized)) return [];
    seen.add(normalized);
    if ([option.question, ...(option.paraphrases ?? [])].some((text) => excluded.has(normalizeQuestion(text)))) return [];
    const words = tokens([option.question, ...(option.paraphrases ?? [])].join(" "));
    let score = 0;
    for (const word of words) {
      if (questionWords.has(word)) score += 3;
      if (answerWords.has(word)) score += 1;
    }
    if (context.page_url && option.page_url === context.page_url) score += 2;
    if (context.anchor && option.anchor === context.anchor) score += 4;
    return [{ option, order, score }];
  }).sort((a, b) => b.score - a.score || a.order - b.order).slice(0, limit).map(({ option }) => option);
}
